import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import * as _ from "lodash";
import { CardDetailModal } from "./CardDetailModal";
import { useMachine } from "@/_zustand/machine";
import { useGetOneClickUser } from "@/hooks/useAuth";
import { useGetOneClickCards } from "@/hooks/useAuth";
import { useAuth } from "@/_zustand/auth";
import { useDeleteOneClickCard } from "@/hooks/useDeleteOneClickCard";
import { AddCardOneClick } from "./AddCardOneClick";
import "./wallet.css";
import Cards from "react-credit-cards-2";
import "react-credit-cards-2/dist/lib/styles.scss";

function useQueryParams() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const WalletForm = ({
  setActiveSection,
  refreshKey,
  triggerWalletRefresh,
}) => {
  const isDevelopment =
    import.meta.env.VITE_APP_ENV === "development" ||
    import.meta.env.VITE_APP_ENV === "testing";
  const query = useQueryParams();
  const [selectedCard, setSelectedCard] = useState(null);
  const { cards, user, authType, tempCard, setTempCard, setCards } = useAuth();
  const { machine } = useMachine();
  const { mutateAsync: getOneClickUserAsync } = useGetOneClickUser();
  const { mutateAsync: getOneClickCardsAsync } = useGetOneClickCards();
  const { confirmAndDeleteCard, isDeleting } = useDeleteOneClickCard();
  const [showAddCardComponent, setShowAddCardComponent] = useState(false);

  const handleRemoveCard = (card) => {
    confirmAndDeleteCard(card, () => triggerWalletRefresh());
  };

  useEffect(() => {
    // Llama a la API para obtener el código de comercio inicial
    if (
      !_.isEmpty(query.get("TBK_TOKEN")) &&
      !_.isUndefined(query.get("TBK_TOKEN"))
    ) {
      setShowAddCardComponent(true);
    } else setShowAddCardComponent(false);
    // Solo debe evaluar el TBK_TOKEN de la URL inicial al montar el componente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadWalletCards = async () => {
      if (authType !== "user" || !user?.usuario_id) {
        return;
      }

      const commerceCodes = [];

      if (isDevelopment) {
        commerceCodes.push(import.meta.env.VITE_CODE_COMERCE_DEV);
      } else {
        if (machine?.oneclick_codigo_comercio) {
          commerceCodes.push(machine.oneclick_codigo_comercio);
        }

        if (tempCard?.codigo_comercio) {
          commerceCodes.push(tempCard.codigo_comercio);
        }

        try {
          const responseCodes = await getOneClickCardsAsync();

          if (Array.isArray(responseCodes)) {
            commerceCodes.push(
              ...responseCodes
                .map((item) => item?.oneclick_codigo_comercio)
                .filter((code) => !_.isEmpty(code)),
            );
          }
        } catch (error) {
          console.error("Error al obtener códigos de comercio:", error);
        }
      }

      const uniqueCodes = _.uniq(
        commerceCodes.filter((code) => !_.isEmpty(code)),
      );

      if (_.isEmpty(uniqueCodes)) {
        return;
      }

      const responses = await Promise.allSettled(
        uniqueCodes.map((codigoComercio) =>
          getOneClickUserAsync({
            usuario_id: user.usuario_id,
            codigo_comercio: codigoComercio,
          }),
        ),
      );

      if (!isMounted) {
        return;
      }

      const successResponses = responses
        .filter((response) => response.status === "fulfilled")
        .map((response) => response.value)
        .filter((cardsResponse) => Array.isArray(cardsResponse));

      if (_.isEmpty(successResponses)) {
        return;
      }

      const mergedCards = _.uniqBy(
        successResponses.flat(),
        (card) => `${card?.codigo_comercio}-${card?.tbk_user}`,
      );

      setCards(mergedCards);

      if (_.isEmpty(mergedCards)) {
        setTempCard({});
      }
    };

    loadWalletCards().catch((error) => {
      console.error("Error al obtener las tarjetas del usuario:", error);
    });

    return () => {
      isMounted = false;
    };
  }, [
    refreshKey,
    authType,
    user?.usuario_id,
    machine?.oneclick_codigo_comercio,
    tempCard?.codigo_comercio,
    isDevelopment,
    getOneClickCardsAsync,
    getOneClickUserAsync,
    setCards,
    setTempCard,
  ]);

  const handleAddCard = () => {
    setShowAddCardComponent(true);
  };

  const handleBack = () => {
    setShowAddCardComponent(false);
    setActiveSection("wallet");
  };

  if (showAddCardComponent)
    return (
      <div className="wallet-container">
        <div className="col-6">
          <AddCardOneClick
            onBackToWallet={handleBack}
            triggerWalletRefresh={triggerWalletRefresh}
          />
        </div>
        <div className="col-6">
          <button className="wallet-add-button" onClick={() => handleBack()}>
            Volver
          </button>
        </div>
      </div>
    );

  if (cards)
    return (
      <div className="wallet-container">
        {selectedCard && (
          <CardDetailModal
            card={selectedCard}
            onClose={() => setSelectedCard(null)}
          />
        )}
        <h2 className="wallet-title">Billetera</h2>
        <div className="wallet-cards">
          {cards &&
            _.uniqBy(cards, "tbk_user")
              .filter(Boolean)
              .map((card, index) => (
                <div key={index} className="credit-card-item">
                  <Cards
                    number={card.card_number}
                    expiry={""}
                    cvc={""}
                    name={card.card_type}
                    focused={"name"} // Pasa la propiedad 'focus'
                    // Renderiza el número de la tarjeta en una máscara
                    placeholders={{ name: "NOMBRE EN TARJETA" }}
                  />

                  <button
                    className="waller-delete-button"
                    onClick={() => handleRemoveCard(card)}
                    disabled={isDeleting}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
        </div>
        <button className="wallet-add-button" onClick={handleAddCard}>
          Agregar nueva tarjeta 💳
        </button>
      </div>
    );

  return (
    <div className="wallet-container">
      <p>No se pudo cargar la información</p>
    </div>
  );
};
